import React, { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  SpecialityFeeTableRowDto,
  SpecialityWiseFeePair,
} from '../../models/MasterAdmin/doctorClinic';
import {
  createDoctorClinicSpecialityFee,
  updateDoctorClinicSpecialityFee,
  UpsertSpecialityFeePayload,
} from '../../services/MasterAdmin/doctorClinicService';
import { SpecialityOption } from '../../services/MasterAdmin/doctorClinicTimeslotService';

type FeeFormState = {
  specialityId: string;
  onlineFirst: string;
  onlineFollow: string;
  offlineFirst: string;
  offlineFollow: string;
};

const emptyPair = (): SpecialityWiseFeePair => ({
  firstVisitFees: '',
  followUpFees: '',
});

const pairFromForm = (first: string, follow: string): SpecialityWiseFeePair => ({
  firstVisitFees: first.trim(),
  followUpFees: follow.trim(),
});

const hasConsultationType = (types: string[], needle: string) =>
  types.some((t) => t.toLowerCase().includes(needle));

interface EditorModalProps {
  open: boolean;
  mode: 'create' | 'edit';
  vendorId: string;
  consultationTypes: string[];
  specialityOptions: SpecialityOption[];
  existingFeeRows: SpecialityFeeTableRowDto[];
  editingRow?: SpecialityFeeTableRowDto | null;
  onClose: () => void;
  onSaved: () => void;
}

const DoctorClinicSpecialityFeeEditorModal: React.FC<EditorModalProps> = ({
  open,
  mode,
  vendorId,
  consultationTypes,
  specialityOptions,
  existingFeeRows,
  editingRow,
  onClose,
  onSaved,
}) => {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState<FeeFormState>({
    specialityId: '',
    onlineFirst: '',
    onlineFollow: '',
    offlineFirst: '',
    offlineFollow: '',
  });
  const [saving, setSaving] = useState(false);

  const showOnline = useMemo(
    () =>
      hasConsultationType(consultationTypes, 'online') ||
      consultationTypes.length === 0,
    [consultationTypes]
  );
  const showOffline = useMemo(
    () =>
      hasConsultationType(consultationTypes, 'offline') ||
      hasConsultationType(consultationTypes, 'in-clinic') ||
      hasConsultationType(consultationTypes, 'in clinic') ||
      consultationTypes.length === 0,
    [consultationTypes]
  );

  const availableSpecialities = useMemo(() => {
    const usedIds = new Set(
      existingFeeRows
        .filter((row) => !isEdit || row.id !== editingRow?.id)
        .map((row) => row.specialityId)
    );
    return specialityOptions.filter((opt) => !usedIds.has(opt.specialityId));
  }, [specialityOptions, existingFeeRows, isEdit, editingRow?.id]);

  useEffect(() => {
    if (!open) return;
    if (isEdit && editingRow) {
      const online = editingRow.feesByConsultationType?.online ?? emptyPair();
      const offline = editingRow.feesByConsultationType?.offline ?? emptyPair();
      setForm({
        specialityId: editingRow.specialityId,
        onlineFirst: online.firstVisitFees ?? '',
        onlineFollow: online.followUpFees ?? '',
        offlineFirst: offline.firstVisitFees ?? '',
        offlineFollow: offline.followUpFees ?? '',
      });
      return;
    }
    setForm({
      specialityId: availableSpecialities[0]?.specialityId ?? '',
      onlineFirst: '',
      onlineFollow: '',
      offlineFirst: '',
      offlineFollow: '',
    });
  }, [open, isEdit, editingRow, availableSpecialities]);

  const validateForm = (): UpsertSpecialityFeePayload | null => {
    if (!isEdit && !form.specialityId.trim()) {
      toast.error('Please select a speciality.');
      return null;
    }

    const online = pairFromForm(form.onlineFirst, form.onlineFollow);
    const offline = pairFromForm(form.offlineFirst, form.offlineFollow);

    const onlineFilled =
      showOnline && (online.firstVisitFees || online.followUpFees);
    const offlineFilled =
      showOffline && (offline.firstVisitFees || offline.followUpFees);

    if (!onlineFilled && !offlineFilled) {
      toast.error('Enter at least one consultation fee.');
      return null;
    }

    const checkLimit = (value: string, label: string) => {
      const n = Number(value);
      if (value.trim() && (!Number.isFinite(n) || n < 0 || n > 10000)) {
        toast.error(`${label} must be between 0 and 10,000.`);
        return false;
      }
      return true;
    };

    const fields = [
      [form.onlineFirst, 'Online first consultation fee'],
      [form.onlineFollow, 'Online follow-up fee'],
      [form.offlineFirst, 'In-clinic first consultation fee'],
      [form.offlineFollow, 'In-clinic follow-up fee'],
    ] as const;

    for (const [value, label] of fields) {
      if (!checkLimit(value, label)) return null;
    }

    return {
      ...(isEdit ? {} : { specialityId: form.specialityId.trim() }),
      feesByConsultationType: {
        ...(showOnline ? { online } : {}),
        ...(showOffline ? { offline } : {}),
      },
    };
  };

  const handleSave = async () => {
    const payload = validateForm();
    if (!payload) return;

    try {
      setSaving(true);
      if (isEdit && editingRow?.id) {
        await updateDoctorClinicSpecialityFee(vendorId, editingRow.id, payload);
        toast.success('Speciality fees updated.');
      } else {
        await createDoctorClinicSpecialityFee(vendorId, payload);
        toast.success('Speciality fees added.');
      }
      onSaved();
      onClose();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to save speciality fees.';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const selectedSpecialityLabel =
    editingRow?.speciality ||
    specialityOptions.find((opt) => opt.specialityId === form.specialityId)?.speciality ||
    '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg rounded-xl bg-white shadow-xl dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isEdit ? 'Edit speciality fees' : 'Add speciality fees'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          {isEdit ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Speciality
              </label>
              <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
                {selectedSpecialityLabel || 'N/A'}
              </p>
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Speciality
              </label>
              <select
                value={form.specialityId}
                onChange={(e) => setForm((prev) => ({ ...prev, specialityId: e.target.value }))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              >
                <option value="">Select speciality</option>
                {availableSpecialities.map((opt) => (
                  <option key={opt.specialityId} value={opt.specialityId}>
                    {opt.speciality}
                  </option>
                ))}
              </select>
              {availableSpecialities.length === 0 ? (
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                  No unused specialities available. Add specializations on the vendor profile first.
                </p>
              ) : null}
            </div>
          )}

          {showOnline ? (
            <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <p className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">Online</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                    First consultation (₹)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={10000}
                    value={form.onlineFirst}
                    onChange={(e) => setForm((prev) => ({ ...prev, onlineFirst: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                    Follow-up (₹)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={10000}
                    value={form.onlineFollow}
                    onChange={(e) => setForm((prev) => ({ ...prev, onlineFollow: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>
            </div>
          ) : null}

          {showOffline ? (
            <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <p className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">In-clinic</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                    First consultation (₹)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={10000}
                    value={form.offlineFirst}
                    onChange={(e) => setForm((prev) => ({ ...prev, offlineFirst: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
                    Follow-up (₹)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={10000}
                    value={form.offlineFollow}
                    onChange={(e) => setForm((prev) => ({ ...prev, offlineFollow: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || (!isEdit && !form.specialityId)}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {saving ? 'Saving...' : isEdit ? 'Save changes' : 'Add fees'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoctorClinicSpecialityFeeEditorModal;
